import { Icon } from '@chakra-ui/react';
import { BasePagination, IconButtonWithTooltip, PlusIcon } from '@elkaso-app/web-design';
import { useGetSupplierStatementsApi } from 'apis/bnpl';
import { SupplierStatementDto } from 'apis/bnpl';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { UploadSupplierStatementModal } from 'pages/bnpl/components/upload-supplier-statement-modal';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import React, { useEffect } from 'react';
import { FiEye } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const SupplierStatementsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isLoading, isFetching, data } = useGetSupplierStatementsApi();

  const { pageCount, total, data: statementsData } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('BNPL - Supplier Statements'));
  }, []);

  interface IColumnsAccessorData {
    id: number;
    restaurant: { id: number; nameEn: string } | undefined;
    supplier: { id: number; name: string } | undefined;
    period: string;
    statementAmount: number;
    originalFilename: string;
    createdAt: string;
    actions: SupplierStatementDto;
  }

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='ID' />,
        accessor: 'id',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['id']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Restaurant' />,
        accessor: 'restaurant',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurant']>) => (
          <TableCell value={value?.nameEn || '---'} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Supplier' />,
        accessor: 'supplier',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplier']>) => (
          <TableCell value={value?.name || '---'} />
        ),
      },
      {
        Header: <TableHeader name='Period' />,
        accessor: 'period',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['period']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Amount' />,
        accessor: 'statementAmount',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['statementAmount']>) => (
          <TableCell value={formatAmount(value)} />
        ),
      },
      {
        Header: <TableHeader name='Document' />,
        accessor: 'originalFilename',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['originalFilename']>) => (
          <TableCell value={value || '---'} />
        ),
      },
      {
        Header: <TableHeader name='Created' />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <IconButtonWithTooltip
            size='sm'
            label='View Details'
            aria-label='view-details-btn'
            Icon={<Icon as={FiEye} />}
            variant='unstyled'
            onClick={() => history.push(`/app/bnpl/supplier-statements/${value?.id}`)}
          />
        ),
      },
    ],
    []
  );

  const columnsAccessorData = React.useMemo(
    () =>
      statementsData?.map((statement): IColumnsAccessorData => {
        return {
          id: statement?.id,
          restaurant: statement?.restaurant,
          supplier: statement?.supplier,
          period: `${statement?.periodStart} — ${statement?.periodEnd}`,
          statementAmount: statement?.statementAmount,
          originalFilename: statement?.originalFilename,
          createdAt: statement?.createdAt,
          actions: statement,
        };
      }),
    [statementsData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Supplier Statements' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />

          <Can I='manage' a='SupplierStatement'>
            <UploadSupplierStatementModal
              trigger={
                <IconButtonWithTooltip
                  label='Upload statement'
                  aria-label='upload-statement-btn'
                  Icon={<PlusIcon />}
                />
              }
            />
          </Can>
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={columnsAccessorData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
